import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "react-query";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";
import SectionTextForm from "./SectionTextForm";
import SectionImagesForm from "./SectionImagesForm";
import SectionScaffold from "./SectionScaffold";
import { StyledButton } from "../StyledComponents";
import { useSectionData } from "../hooks/useSections"; // assuming this exists

const onSuccess = (query) => (data) => {
  if (query === "sections-get") {
    console.log("Section fetched successfully", data);
  }
};

const onError = (query) => (error) => {
  if (query === "sections-get") {
    console.error("Error fetching section", error);
  }
};

export default function EditSection({ token }) {
  const navigate = useNavigate();
  const { sectionId } = useParams();
  const [searchParams] = useSearchParams();
  const homeItemType = searchParams.get("homeItemType");

  const allowedTypes = ["place", "bigEvent"];
  const isValidHomeItemType = allowedTypes.includes(homeItemType);

  const { data, isLoading, isError } = useSectionData(
    onSuccess,
    onError,
    token.token,
    sectionId,
    homeItemType === "bigEvent"
  );

  const [values, setValues] = useState({
    title: "",
    subtitle: "",
    text: "",
    priority: "",
  });
  const [images, setImages] = useState([]);
  const [picsChanged, setPicsChanged] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // --- Initialize Form when Section Loads ---
  useEffect(() => {
    if (data) {
      setValues({
        title: data.title || "",
        subtitle: data.subtitle || "",
        text: data.text || "",
        priority: data.priority?.toString() || "",
      });
    }
  }, [data]);

  useEffect(() => {
    const fetchPics = async () => {
      console.log(data);
      if (data.photos) {
        const lst = await Promise.all(
          data.photos.map(async (photo) => {
            const res = await axios.get(
              `${process.env.REACT_APP_BASE_URL}:${process.env.REACT_APP_ADMIN_PORT}/sections/file/${photo}`,
              {
                responseType: "arraybuffer",
                headers: {
                  Accept: "*/*",
                  token: token.token,
                },
              }
            );

            const blob = new Blob([res.data], {
              type: "image/jpeg",
            });
            console.log("blob: ", blob);
            const objectURL = URL.createObjectURL(blob);
            const file = new File([blob], photo, { lastModified: Date.now() });
            console.log(file);
            return { data_url: objectURL, file: file };
          })
        );

        setImages(lst);
      }
    };

    fetchPics();
  }, [data, token]);

  const queryClient = useQueryClient();

  const editSection = useMutation(
    () => {
      const headers = {
        Accept: "*/*",
        token: token.token,
        "Content-Type": "multipart/form-data",
      };

      const formData = new FormData();

      if (picsChanged) {
        images.forEach((image, i) => {
          formData.append("pics", image.file);
        });
      }

      formData.append(
        "data",
        JSON.stringify({
          title: values.title || null,
          subtitle: values.subtitle || null,
          text: values.text || null,
          priority: values.priority ? parseInt(values.priority, 10) : null,
        })
      );

      return axios.put(
        `${process.env.REACT_APP_BASE_URL}:${process.env.REACT_APP_ADMIN_PORT}/sections/edit_section/${sectionId}`,
        formData,
        { headers }
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["sections-get"]);
        setShowSuccess(true);
      },
    }
  );

  // --- Redirect if invalid type ---
  useEffect(() => {
    if (!isValidHomeItemType) {
      navigate("/", { replace: true });
    }
  }, [isValidHomeItemType, navigate]);

  if (!isValidHomeItemType) return null;

  const handleCloseSuccess = (event, reason) => {
    if (reason === "clickaway") return;
    setShowSuccess(false);
    navigate(-1);
  };

  const handleDiscard = () => navigate(-1);

  if (isLoading) {
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress sx={{ color: "#1EABF1" }} />
      </div>
    );
  }

  if (isError) {
    return (
      <Alert severity="error">
        Could not load section. Please try again later.
      </Alert>
    );
  }

  return (
    <>
      <SectionScaffold
        title="Edit Section"
        cancelText="Discard Changes"
        onCancel={handleDiscard}
        topChildren={<SectionTextForm values={values} setValues={setValues} />}
        bottomChildren={
          <SectionImagesForm
            images={images}
            setImages={(updatedImages) => {
              setImages(updatedImages);
              setPicsChanged(true);
            }}
            setDidChange={setPicsChanged}
          />
        }
        footer={
          editSection.isLoading ? (
            <CircularProgress sx={{ color: "#1EABF1" }} />
          ) : (
            <StyledButton
              variant="contained"
              size="large"
              width="30%"
              disabled={!values.title}
              onClick={() => editSection.mutate()}
              myColor="#1EABF1"
              hoverColor="#187DDA"
            >
              Save Changes
            </StyledButton>
          )
        }
        footerNote="You can update this Section at any time"
      />

      {/* Error Snackbar */}
      <Snackbar
        open={editSection.isError}
        autoHideDuration={2000}
        onClose={() => editSection.reset()}
      >
        <Alert
          onClose={() => editSection.reset()}
          severity="error"
          sx={{ width: "100%" }}
        >
          An error occured!
        </Alert>
      </Snackbar>
      <Snackbar
        open={showSuccess}
        autoHideDuration={2000}
        onClose={handleCloseSuccess}
      >
        <Alert
          onClose={handleCloseSuccess}
          severity="success"
          sx={{ width: "100%" }}
        >
          Changes saved successfully!
        </Alert>
      </Snackbar>
    </>
  );
}
